import { useFreemiumUrlState } from './use-freemium-url-state';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFreemiumCheckoutStore } from '../stores/freemium-checkout-store';

import { freemiumApi, freemiumKeys } from '@client/api/waterlily';
import { API } from '@/api/types';

import type { FreemiumTier } from '@shared';

interface SelectionVariables {
  tier: FreemiumTier;
  payNow: boolean;
}

export function useFreemiumSelection(clientId: string) {
  const queryClient = useQueryClient();
  const { intent, selectedTier, startLicensedConfirm, startCheckout, goToLicensedDone } =
    useFreemiumUrlState();
  const setClientSecret = useFreemiumCheckoutStore(state => state.setClientSecret);

  const returnPath = `/clients/${clientId}/onboarding/funding-sources`;

  const mutation = useMutation({
    mutationFn: ({ tier, payNow }: SelectionVariables) =>
      freemiumApi.postSelection(clientId, {
        tier,
        intent: intent ?? undefined,
        payNow,
        returnPath,
        uiMode: 'embedded',
      }),
    onSuccess: (result: API.FreemiumSelectionResponse, variables: SelectionVariables) => {
      if (result.requiresPayment && result.clientSecret) {
        setClientSecret(result.clientSecret);
        startCheckout(variables.tier);
        return;
      }

      queryClient.invalidateQueries({ queryKey: freemiumKeys.planningUnlock(clientId) });
      goToLicensedDone();
    },
  });

  function chooseTier(tier: FreemiumTier, isLicensed: boolean) {
    if (isLicensed) {
      startLicensedConfirm(tier);
      return;
    }
    mutation.mutate({ tier, payNow: true });
  }

  function payLicensedNow() {
    if (selectedTier) mutation.mutate({ tier: selectedTier, payNow: true });
  }

  function waitForOutreach() {
    if (selectedTier) mutation.mutate({ tier: selectedTier, payNow: false });
  }

  function recreateCheckout(tier: FreemiumTier) {
    mutation.mutate({ tier, payNow: true });
  }

  return {
    chooseTier,
    payLicensedNow,
    waitForOutreach,
    recreateCheckout,
    isPending: mutation.isPending,
    isError: mutation.isError,
  };
}
